import React, { useEffect, useMemo, useRef } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useAuditYear } from '../../../contexts/AuditYearContext.tsx';
import { fetchSoxCycles, fetchProcessesForCycle } from '../../../request/sox';
import {
  extractMappedActorsAndSystems,
  fetchFlowchart,
  fetchFlowchartVersions,
  normalizeFlowchartVersionOption,
} from '../../../request/flowchart';
import { processesQueryKey } from '../../../queries/sox';
import { flowchartVersionsQueryKey } from '../../../queries/flowchart';

export type PlatformCycleSelection = {
  cycleId: string;
  cycleBusinessId: string;
};

export type PlatformProcessSelection = {
  cycleId: string;
  cycleBusinessId: string;
  processId: string;
  processBusinessId: string;
  flowchartVersionId: string;
};

export type PlatformContextSelection = {
  cycles: PlatformCycleSelection[];
  processes: PlatformProcessSelection[];
};

export type PlatformCatalogRef = { id: string; name: string };

export type PlatformResolvedProcessCatalog = {
  key: string;
  cycleId: string;
  cycleBusinessId: string;
  cycleLabel: string;
  processId: string;
  processBusinessId: string;
  processLabel: string;
  flowchartVersionId: string;
  versionLabel: string;
  actors: PlatformCatalogRef[];
  systems: PlatformCatalogRef[];
};

export type PlatformResolvedCatalog = {
  perProcess: PlatformResolvedProcessCatalog[];
  actors: PlatformCatalogRef[];
  systems: PlatformCatalogRef[];
};

type Props = {
  value: PlatformContextSelection | null;
  onChange: (next: PlatformContextSelection | null) => void;
  onResolvedCatalogChange?: (next: PlatformResolvedCatalog | null) => void;
  /** True while roles/systems catalog is loading for selected process versions. */
  onCatalogLoadingChange?: (loading: boolean) => void;
};

type ProcessRow = {
  id?: string;
  process_id?: string;
  name?: string;
  process_name?: string;
};

type CatalogRef = PlatformCatalogRef;

type CycleRow = {
  id: string;
  cycle_id?: string;
  name?: string;
};

function isPublishedActive(version: ReturnType<typeof normalizeFlowchartVersionOption>) {
  return version.status === 'published' && version.isActive === true;
}

function processLabel(p: ProcessRow) {
  const name = String(p.name || p.process_name || '').trim();
  const code = String(p.process_id || '').trim();
  if (name && code && name !== code) return `${name} (${code})`;
  return name || code || 'Untitled process';
}

function cycleLabel(c: CycleRow) {
  return c.name || c.cycle_id || c.id;
}

function mergeCatalogRefs(groups: Array<{ actors: CatalogRef[]; systems: CatalogRef[] }>) {
  const actorsById = new Map<string, CatalogRef>();
  const systemsById = new Map<string, CatalogRef>();
  for (const group of groups) {
    for (const a of group.actors) {
      if (a.id && a.name && !actorsById.has(a.id)) actorsById.set(a.id, a);
    }
    for (const s of group.systems) {
      if (s.id && s.name && !systemsById.has(s.id)) systemsById.set(s.id, s);
    }
  }
  const byName = (a: CatalogRef, b: CatalogRef) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  return {
    actors: [...actorsById.values()].sort(byName),
    systems: [...systemsById.values()].sort(byName),
  };
}

function ActorChip({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-800">
      <span className="h-2 w-2 shrink-0 rounded-full bg-red-500" aria-hidden="true" />
      {name}
    </span>
  );
}

function SystemChip({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-800">
      <span className="h-2 w-2 shrink-0 rounded-full bg-blue-500" aria-hidden="true" />
      {name}
    </span>
  );
}

function CatalogChipSection({
  title,
  actors,
  systems,
}: {
  title: string;
  actors: CatalogRef[];
  systems: CatalogRef[];
}) {
  return (
    <div className="rounded-md border border-gray-200 bg-white p-3">
      <h4 className="text-sm font-medium text-gray-800">{title}</h4>
      <div className="mt-2 space-y-2">
        <div>
          <p className="text-xs font-medium text-gray-500">Roles</p>
          {actors.length === 0 ? (
            <p className="mt-1 text-xs text-gray-400">None</p>
          ) : (
            <div className="mt-1 flex flex-wrap gap-1.5">
              {actors.map((a) => (
                <ActorChip key={a.id} name={a.name} />
              ))}
            </div>
          )}
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500">Systems</p>
          {systems.length === 0 ? (
            <p className="mt-1 text-xs text-gray-400">None</p>
          ) : (
            <div className="mt-1 flex flex-wrap gap-1.5">
              {systems.map((s) => (
                <SystemChip key={s.id} name={s.name} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Multi cycle → multi process → published+active version per process → roles/systems union. */
export default function PlatformContextPicker({
  value,
  onChange,
  onResolvedCatalogChange,
  onCatalogLoadingChange,
}: Props) {
  const { selectedAuditYearId } = useAuditYear();

  const selectedCycles = value?.cycles ?? [];
  const selectedProcesses = value?.processes ?? [];
  const selectedCycleIds = useMemo(() => selectedCycles.map((c) => c.cycleId), [selectedCycles]);
  const selectedProcessKeys = useMemo(
    () => new Set(selectedProcesses.map((p) => `${p.cycleId}:${p.processId}`)),
    [selectedProcesses],
  );

  const cyclesQuery = useQuery({
    queryKey: selectedAuditYearId ? ['sox-cycles', selectedAuditYearId] : ['sox-cycles'],
    queryFn: () => fetchSoxCycles(selectedAuditYearId),
    enabled: Boolean(selectedAuditYearId),
  });

  const cycles = (cyclesQuery.data ?? []) as CycleRow[];
  const cycleById = useMemo(() => {
    const map = new Map<string, CycleRow>();
    for (const c of cycles) map.set(c.id, c);
    return map;
  }, [cycles]);

  const processQueries = useQueries({
    queries: selectedCycles.map((cycle) => ({
      queryKey: [...processesQueryKey(cycle.cycleBusinessId || '—'), selectedAuditYearId ?? ''],
      queryFn: () => fetchProcessesForCycle(cycle.cycleBusinessId, selectedAuditYearId),
      enabled: Boolean(cycle.cycleBusinessId && selectedAuditYearId),
    })),
  });

  const processesByCycleId = useMemo(() => {
    const map = new Map<string, ProcessRow[]>();
    selectedCycles.forEach((cycle, i) => {
      map.set(cycle.cycleId, (processQueries[i]?.data ?? []) as ProcessRow[]);
    });
    return map;
  }, [selectedCycles, processQueries]);

  const processLookup = useMemo(() => {
    const map = new Map<string, ProcessRow>();
    for (const [cycleId, rows] of processesByCycleId) {
      for (const p of rows) {
        if (p.id) map.set(`${cycleId}:${p.id}`, p);
      }
    }
    return map;
  }, [processesByCycleId]);

  const versionQueries = useQueries({
    queries: selectedProcesses.map((sel) => ({
      queryKey: flowchartVersionsQueryKey(
        sel.cycleBusinessId || '—',
        sel.processBusinessId || '—',
        selectedAuditYearId,
      ),
      queryFn: () =>
        fetchFlowchartVersions(sel.cycleBusinessId, sel.processBusinessId, selectedAuditYearId),
      enabled: Boolean(sel.cycleBusinessId && sel.processBusinessId && selectedAuditYearId),
    })),
  });

  const publishedActiveByKey = useMemo(() => {
    const map = new Map<string, ReturnType<typeof normalizeFlowchartVersionOption>[]>();
    selectedProcesses.forEach((sel, i) => {
      const key = `${sel.cycleId}:${sel.processId}`;
      map.set(key, (versionQueries[i]?.data ?? []).filter(isPublishedActive));
    });
    return map;
  }, [selectedProcesses, versionQueries]);

  const selectionsReadyForCatalog = useMemo(() => {
    return selectedProcesses
      .map((sel) => {
        const key = `${sel.cycleId}:${sel.processId}`;
        const versions = publishedActiveByKey.get(key) ?? [];
        const version = versions.find((v) => v.flowchartVersionId === sel.flowchartVersionId);
        if (!sel.flowchartVersionId || version?.value == null) return null;
        const row = processLookup.get(key);
        const cycleMeta = cycleById.get(sel.cycleId);
        return {
          key,
          cycleId: sel.cycleId,
          cycleBusinessId: sel.cycleBusinessId,
          processId: sel.processId,
          processBusinessId: sel.processBusinessId,
          versionNumber: version.value,
          flowchartVersionId: sel.flowchartVersionId,
          versionLabel: version.label || `v${version.value}`,
          processLabel: row ? processLabel(row) : sel.processBusinessId || sel.processId,
          cycleLabel: cycleMeta ? cycleLabel(cycleMeta) : sel.cycleBusinessId,
        };
      })
      .filter(Boolean) as Array<{
      key: string;
      cycleId: string;
      cycleBusinessId: string;
      processId: string;
      processBusinessId: string;
      versionNumber: number;
      flowchartVersionId: string;
      versionLabel: string;
      processLabel: string;
      cycleLabel: string;
    }>;
  }, [selectedProcesses, publishedActiveByKey, processLookup, cycleById]);

  const mappedCatalogQuery = useQuery({
    queryKey: [
      'sop-flowchart-mapped-catalog',
      selectedAuditYearId ?? '',
      selectionsReadyForCatalog.map((s) => s.flowchartVersionId).sort().join(','),
    ],
    queryFn: async () => {
      const perProcess: PlatformResolvedProcessCatalog[] = await Promise.all(
        selectionsReadyForCatalog.map(async (sel) => {
          const data = await fetchFlowchart(sel.cycleBusinessId, sel.processBusinessId, {
            versionNumber: sel.versionNumber,
            auditYearId: selectedAuditYearId,
          });
          const catalog = extractMappedActorsAndSystems(data?.flowchartData);
          return {
            key: sel.key,
            cycleId: sel.cycleId,
            cycleBusinessId: sel.cycleBusinessId,
            cycleLabel: sel.cycleLabel,
            processId: sel.processId,
            processBusinessId: sel.processBusinessId,
            processLabel: sel.processLabel,
            flowchartVersionId: sel.flowchartVersionId,
            versionLabel: sel.versionLabel,
            actors: catalog.actors,
            systems: catalog.systems,
          };
        }),
      );
      const combined = mergeCatalogRefs(perProcess);
      return {
        perProcess,
        combined,
      };
    },
    enabled: Boolean(selectedAuditYearId && selectionsReadyForCatalog.length > 0),
  });

  const perProcessCatalog = mappedCatalogQuery.data?.perProcess ?? [];
  const combinedActors = mappedCatalogQuery.data?.combined.actors ?? [];
  const combinedSystems = mappedCatalogQuery.data?.combined.systems ?? [];

  // Keep parent create payload in sync with per-process + combined catalog (or clear it).
  useEffect(() => {
    if (!onResolvedCatalogChange) return;
    if (!value || selectionsReadyForCatalog.length === 0) {
      onResolvedCatalogChange(null);
      return;
    }
    if (mappedCatalogQuery.isLoading || mappedCatalogQuery.isFetching) return;
    if (mappedCatalogQuery.isError || !mappedCatalogQuery.data?.combined) {
      onResolvedCatalogChange(null);
      return;
    }
    onResolvedCatalogChange({
      perProcess: mappedCatalogQuery.data.perProcess,
      actors: mappedCatalogQuery.data.combined.actors,
      systems: mappedCatalogQuery.data.combined.systems,
    });
  }, [
    onResolvedCatalogChange,
    value,
    selectionsReadyForCatalog.length,
    mappedCatalogQuery.isLoading,
    mappedCatalogQuery.isFetching,
    mappedCatalogQuery.isError,
    mappedCatalogQuery.data,
  ]);

  useEffect(() => {
    if (!onCatalogLoadingChange) return;
    const needed = selectionsReadyForCatalog.length > 0;
    onCatalogLoadingChange(needed && (mappedCatalogQuery.isLoading || mappedCatalogQuery.isFetching));
  }, [
    onCatalogLoadingChange,
    selectionsReadyForCatalog.length,
    mappedCatalogQuery.isLoading,
    mappedCatalogQuery.isFetching,
  ]);

  const emit = (next: PlatformContextSelection | null) => {
    if (!next || (next.cycles.length === 0 && next.processes.length === 0)) {
      onChange(null);
      return;
    }
    onChange(next);
  };

  // Audit year change clears everything.
  const prevAuditYearIdRef = useRef(selectedAuditYearId);
  useEffect(() => {
    if (prevAuditYearIdRef.current === selectedAuditYearId) return;
    prevAuditYearIdRef.current = selectedAuditYearId;
    onChange(null);
  }, [selectedAuditYearId, onChange]);

  // Drop cycles that disappeared from the year list.
  useEffect(() => {
    if (selectedCycles.length === 0) return;
    if (cyclesQuery.isLoading || cyclesQuery.isFetching) return;
    const nextCycles = selectedCycles.filter((c) => cycleById.has(c.cycleId));
    if (nextCycles.length === selectedCycles.length) return;
    const keepCycleIds = new Set(nextCycles.map((c) => c.cycleId));
    emit({
      cycles: nextCycles,
      processes: selectedProcesses.filter((p) => keepCycleIds.has(p.cycleId)),
    });
  }, [
    cycleById,
    selectedCycles,
    selectedProcesses,
    cyclesQuery.isLoading,
    cyclesQuery.isFetching,
  ]);

  // Drop processes no longer under their cycle; refresh business ids.
  useEffect(() => {
    if (selectedProcesses.length === 0) return;
    if (processQueries.some((q) => q.isLoading || q.isFetching)) return;

    let changed = false;
    const nextProcesses: PlatformProcessSelection[] = [];
    for (const sel of selectedProcesses) {
      if (!selectedCycleIds.includes(sel.cycleId)) {
        changed = true;
        continue;
      }
      const row = processLookup.get(`${sel.cycleId}:${sel.processId}`);
      if (!row) {
        changed = true;
        continue;
      }
      const businessId = row.process_id ?? row.id ?? sel.processBusinessId;
      if (businessId !== sel.processBusinessId) {
        changed = true;
        nextProcesses.push({ ...sel, processBusinessId: businessId, flowchartVersionId: '' });
        continue;
      }
      nextProcesses.push(sel);
    }
    if (changed) {
      emit({
        cycles: selectedCycles,
        processes: nextProcesses,
      });
    }
  }, [
    processLookup,
    selectedProcesses,
    selectedCycles,
    selectedCycleIds,
    processQueries,
  ]);

  // Clear version if no longer published+active.
  useEffect(() => {
    if (selectedProcesses.length === 0) return;
    if (versionQueries.some((q) => q.isLoading || q.isFetching)) return;

    let changed = false;
    const nextProcesses = selectedProcesses.map((sel) => {
      if (!sel.flowchartVersionId) return sel;
      const versions = publishedActiveByKey.get(`${sel.cycleId}:${sel.processId}`) ?? [];
      if (versions.some((v) => v.flowchartVersionId === sel.flowchartVersionId)) return sel;
      changed = true;
      return { ...sel, flowchartVersionId: '' };
    });
    if (changed) {
      emit({
        cycles: selectedCycles,
        processes: nextProcesses,
      });
    }
  }, [selectedProcesses, selectedCycles, publishedActiveByKey, versionQueries]);

  const toggleCycle = (cycleId: string, checked: boolean) => {
    if (!checked) {
      emit({
        cycles: selectedCycles.filter((c) => c.cycleId !== cycleId),
        processes: selectedProcesses.filter((p) => p.cycleId !== cycleId),
      });
      return;
    }
    if (selectedCycleIds.includes(cycleId)) return;
    const cycle = cycleById.get(cycleId);
    if (!cycle) return;
    emit({
      cycles: [
        ...selectedCycles,
        {
          cycleId,
          cycleBusinessId: cycle.cycle_id ?? cycle.id,
        },
      ],
      processes: selectedProcesses,
    });
  };

  const toggleProcess = (
    cycleId: string,
    cycleBusinessId: string,
    processId: string,
    checked: boolean,
  ) => {
    const key = `${cycleId}:${processId}`;
    if (!checked) {
      emit({
        cycles: selectedCycles,
        processes: selectedProcesses.filter((p) => `${p.cycleId}:${p.processId}` !== key),
      });
      return;
    }
    if (selectedProcessKeys.has(key)) return;
    const row = processLookup.get(key);
    if (!row) return;
    emit({
      cycles: selectedCycles,
      processes: [
        ...selectedProcesses,
        {
          cycleId,
          cycleBusinessId,
          processId,
          processBusinessId: row.process_id ?? row.id ?? '',
          flowchartVersionId: '',
        },
      ],
    });
  };

  const setProcessVersion = (cycleId: string, processId: string, flowchartVersionId: string) => {
    const key = `${cycleId}:${processId}`;
    emit({
      cycles: selectedCycles,
      processes: selectedProcesses.map((p) =>
        `${p.cycleId}:${p.processId}` === key ? { ...p, flowchartVersionId } : p,
      ),
    });
  };

  const processesLoading = processQueries.some((q) => q.isLoading);
  const processesError = processQueries.some((q) => q.isError);
  const showCatalog = selectionsReadyForCatalog.length > 0;

  return (
    <fieldset className="space-y-4 rounded-md border border-gray-200 bg-gray-50/60 p-4">
      <legend className="px-1 text-sm font-medium text-gray-800">Platform context</legend>

      {!selectedAuditYearId ? (
        <p className="text-sm text-amber-800">Select an audit year in the sidebar to load cycles.</p>
      ) : (
        <>
          <div>
            <span className="block text-sm font-medium text-gray-700">Cycles</span>
            {cyclesQuery.isLoading ? (
              <p className="mt-1 text-xs text-gray-500">Loading cycles…</p>
            ) : cyclesQuery.isError ? (
              <p className="mt-1 text-xs text-red-600">Could not load cycles.</p>
            ) : cycles.length === 0 ? (
              <p className="mt-1 text-xs text-amber-700">No cycles for this audit year.</p>
            ) : (
              <ul className="mt-1 max-h-40 space-y-1 overflow-y-auto rounded-md border border-gray-300 bg-white p-2">
                {cycles.map((c) => {
                  const checked = selectedCycleIds.includes(c.id);
                  return (
                    <li key={c.id}>
                      <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm text-gray-800 hover:bg-gray-50">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => toggleCycle(c.id, e.target.checked)}
                        />
                        <span>{cycleLabel(c)}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div>
            <span className="block text-sm font-medium text-gray-700">Processes</span>
            {selectedCycles.length === 0 ? (
              <p className="mt-1 text-xs text-gray-500">Select a cycle first</p>
            ) : processesLoading ? (
              <p className="mt-1 text-xs text-gray-500">Loading processes…</p>
            ) : processesError ? (
              <p className="mt-1 text-xs text-red-600">Could not load processes.</p>
            ) : (
              <div className="mt-1 max-h-56 space-y-3 overflow-y-auto rounded-md border border-gray-300 bg-white p-2">
                {selectedCycles.map((cycle, cycleIndex) => {
                  const rows = processesByCycleId.get(cycle.cycleId) ?? [];
                  const cycleMeta = cycleById.get(cycle.cycleId);
                  const q = processQueries[cycleIndex];
                  return (
                    <div key={cycle.cycleId}>
                      <p className="mb-1 text-xs font-medium text-gray-600">
                        {cycleMeta ? cycleLabel(cycleMeta) : cycle.cycleBusinessId}
                      </p>
                      {q?.isError ? (
                        <p className="text-xs text-red-600">Could not load processes.</p>
                      ) : rows.length === 0 ? (
                        <p className="text-xs text-amber-700">No processes for this cycle.</p>
                      ) : (
                        <ul className="space-y-1">
                          {rows.map((p) => {
                            if (!p.id) return null;
                            const key = `${cycle.cycleId}:${p.id}`;
                            const checked = selectedProcessKeys.has(key);
                            return (
                              <li key={key}>
                                <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm text-gray-800 hover:bg-gray-50">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) =>
                                      toggleProcess(
                                        cycle.cycleId,
                                        cycle.cycleBusinessId,
                                        p.id!,
                                        e.target.checked,
                                      )
                                    }
                                  />
                                  <span>{processLabel(p)}</span>
                                </label>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {selectedProcesses.length > 0 ? (
            <div className="space-y-3">
              <span className="block text-sm font-medium text-gray-700">Flowchart versions</span>
              {selectedProcesses.map((sel, index) => {
                const row = processLookup.get(`${sel.cycleId}:${sel.processId}`);
                const cycleMeta = cycleById.get(sel.cycleId);
                const versions = publishedActiveByKey.get(`${sel.cycleId}:${sel.processId}`) ?? [];
                const q = versionQueries[index];
                const procLabel = row ? processLabel(row) : sel.processBusinessId || sel.processId;
                const cycLabel = cycleMeta ? cycleLabel(cycleMeta) : sel.cycleBusinessId;
                return (
                  <div key={`${sel.cycleId}:${sel.processId}`}>
                    <label
                      htmlFor={`sop-flowchart-${sel.cycleId}-${sel.processId}`}
                      className="block text-xs font-medium text-gray-600"
                    >
                      {cycLabel} · {procLabel}
                    </label>
                    <select
                      id={`sop-flowchart-${sel.cycleId}-${sel.processId}`}
                      className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm disabled:bg-gray-100"
                      value={sel.flowchartVersionId}
                      onChange={(e) =>
                        setProcessVersion(sel.cycleId, sel.processId, e.target.value)
                      }
                      disabled={q?.isLoading}
                    >
                      <option value="">
                        {q?.isLoading ? 'Loading versions…' : 'Select a published flowchart'}
                      </option>
                      {versions.map((v) => (
                        <option key={v.flowchartVersionId} value={v.flowchartVersionId ?? ''}>
                          {v.label}
                          {v.isActive ? ' (active)' : ''}
                        </option>
                      ))}
                    </select>
                    {q?.isError && (
                      <p className="mt-1 text-xs text-red-600">Could not load flowchart versions.</p>
                    )}
                    {!q?.isLoading && !q?.isError && versions.length === 0 && (
                      <p className="mt-1 text-xs text-amber-700">
                        No published active flowchart for this process.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : null}

          {showCatalog ? (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">Roles &amp; systems</h3>
              {mappedCatalogQuery.isLoading ? (
                <p className="text-xs text-gray-500">Loading…</p>
              ) : mappedCatalogQuery.isError ? (
                <p className="text-xs text-red-600">Could not load roles and systems.</p>
              ) : (
                <>
                  <div className="space-y-2">
                    {perProcessCatalog.map((group) => (
                      <CatalogChipSection
                        key={group.key}
                        title={`${group.cycleLabel} · ${group.processLabel} · ${group.versionLabel}`}
                        actors={group.actors}
                        systems={group.systems}
                      />
                    ))}
                  </div>
                  <CatalogChipSection
                    title="Combined (unique)"
                    actors={combinedActors}
                    systems={combinedSystems}
                  />
                </>
              )}
            </div>
          ) : null}
        </>
      )}
    </fieldset>
  );
}
